// Result-entry prefill for the match dialog (useFixtureForm), kept here so it is
// unit tested with real Vue watchers (tests/unit/x198-result-prefill.test.ts).
//
// X198 (Grant 2026-10-27, "the existing result entered wasn't prefilled"): the
// "scoring system changed" watcher clears the picked result when the system has
// no game grid and nothing is in gameData. A system without split games and
// without numberGames (the schema v1 stock "Single set, exact score" and 15 more)
// leaves gameData empty after a load, so the watcher, running after the load set
// homeResult/awayResult from the saved match, wiped them: the dialog opened blank.
// Systems with numberGames padded gameData with blank rows, which skipped the
// watcher, so only these systems showed it. The reset is for picking ANOTHER
// system (new friendly, a linker's system); the system a saved match was just
// loaded with keeps its result.
import {toRaw, watch} from 'vue';
import _ from 'lodash';

// What the dialog starts from for a saved (or unplayed) match.
export function resultStateFromFixture(fixture) {
    const scoringSystem = _.cloneDeep(fixture.scoringSystem);
    const games = Array.isArray(fixture.games) ? fixture.games : [];
    // A played fixture recorded without game detail reopens in overall mode; an
    // unplayed one keeps the system's game entry available for first-time entry.
    // Set-score-required fixtures never drop to overall mode — the grid is the
    // only full-detail entry (rowless historic results reopen empty to be filled).
    if (scoringSystem && fixture.playedAt != null && games.length === 0 &&
        fixture.requiresGameScores !== true) {
        scoringSystem.splitGames = false;
    }
    const gameData = scoringSystem?.splitGames
        ? _.sortBy(games.map(g => ({...g})), g => g.sort ?? 0)
        : [];
    while (gameData.length < (scoringSystem?.numberGames ?? 0)) gameData.push({});
    return {
        scoringSystem,
        gameData,
        homeResult: fixture.homeTeamResult ?? null,
        awayResult: fixture.awayTeamResult ?? null,
    };
}

// Wires the refs: returns applyFixture(fixture) and installs the system watcher.
export function useResultPrefill({scoringSystem, gameData, homeResult, awayResult}) {
    // the system object a saved match was loaded with (identity, not id: the
    // same system picked again elsewhere is a fresh object and still resets)
    let loadedSystem = null;

    const applyFixture = (fixture) => {
        const state = resultStateFromFixture(fixture);
        loadedSystem = state.scoringSystem;
        gameData.value = state.gameData;
        scoringSystem.value = state.scoringSystem;
        homeResult.value = state.homeResult;
        awayResult.value = state.awayResult;
    };

    watch(scoringSystem, (newVal) => {
        if (!newVal || gameData.value.length > 0) return;
        gameData.value = Array.from({length: newVal.numberGames ?? 0}, () => ({homeTeamScore: null, awayTeamScore: null}));
        if (!newVal.splitGames && toRaw(newVal) !== loadedSystem) homeResult.value = awayResult.value = null;
    });

    return {applyFixture};
}
